import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import AuthLayout from '../../components/layouts/AuthLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Loader2, Lock, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../../services/api';

const ResetPasswordPage = () => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      return;
    }
    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password-reset', null, { params: { token, new_password: password } });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Token ungültig oder abgelaufen. Bitte fordern Sie einen neuen Link an.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div className="glass-card p-8 text-center" data-testid="reset-no-token">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-heading text-xl font-bold text-white mb-2">Ungültiger Link</h1>
          <p className="text-slate-400 text-sm mb-6">
            Dieser Link ist ungültig. Bitte fordern Sie einen neuen Link an.
          </p>
          <Link to="/forgot-password" className="text-[#EC2229] hover:underline text-sm">
            Neuen Link anfordern
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="glass-card p-8" data-testid="reset-password-form">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl font-bold text-white mb-2">Neues Passwort setzen</h1>
          <p className="text-slate-400 text-sm">Geben Sie Ihr neues Passwort ein.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {success ? (
          <div className="space-y-6">
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-emerald-400 font-medium">Passwort erfolgreich geändert</p>
                <p className="text-sm text-slate-400 mt-1">
                  Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
                </p>
              </div>
            </div>
            <Button
              className="btn-primary w-full"
              onClick={() => navigate('/login')}
              data-testid="go-to-login"
            >
              Zum Login
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="label-text">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Neues Passwort"
                  className="input-field pl-10"
                  required
                  data-testid="reset-password"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="label-text">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort bestätigen"
                  className="input-field pl-10"
                  required
                  data-testid="reset-confirm-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="reset-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                'Passwort speichern'
              )}
            </Button>

            <div className="text-center">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zum Login
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
};

export default ResetPasswordPage;
